import { createRouter, createWebHistory } from 'vue-router'
import { getAuth, onAuthStateChanged } from 'firebase/auth'

import MainPage from '@/views/MainPage.vue'
import BuildCardsPage from '@/views/BuildCardsPage.vue'
import BuildBoardsPage from '@/views/BuildBoardsPage.vue'
import LoginPage from '@/views/LoginPage.vue'
import RegisterPage from '@/views/RegisterPage.vue'
import CreateBoardGamePage from '@/views/CreateBoardGamePage.vue'
import BoardGameDetailPage from '@/views/BoardGameDetailPage.vue'
import UserSettingsPage from '@/views/UserSettingsPage.vue'
import CardDetailPage from '@/views/CardDetailPage.vue'
import GameDesignerPage from '@/views/GameDesignerPage.vue'
import PlaytestReviewPage from '@/views/PlaytestReviewPage.vue'
import NotFoundPage from '@/views/NotFoundPage.vue'


import store from '../store';


const routes = [
  {
    path: '/',
    name: 'Home',
    component: MainPage
  },
  // The signed-in home. `/` still has to sell the product to a first-time
  // visitor; this is the page for someone who already has games.
  {
    path: '/main',
    name: 'Main',
    component: () => import('../views/MainAppPage.vue'),
    meta: { requiresAuth: true }
  },
  // Every game you can reach: owned, shared with you, and followed.
  {
    path: '/board-games',
    name: 'MyGames',
    component: () => import('../views/MyGamesPage.vue'),
    meta: { requiresAuth: true }
  },
  // The page was first built at /my-games; keep it working.
  {
    path: '/my-games',
    name: 'MyGamesLegacy',
    component: () => import('../views/MyGamesPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/build-cards',
    name: 'BuildCards',
    component: BuildCardsPage,
    meta: { requiresAuth: true }
  },
  // The list of a game's boards. Public, like the board pages it links to.
  {
    path: '/build-boards/:boardGameId',
    name: 'BuildBoards',
    component: () => import('../views/BoardListPage.vue')
  },
  {
    path: '/build-boards/:boardGameId/new',
    name: 'BoardNew',
    component: BuildBoardsPage,
    meta: { requiresAuth: true }
  },
  // A board has its own URL so an agent can hand you a link to look at, and
  // so a playtester can print it. Public, like the game page.
  {
    path: '/boards/:boardId',
    name: 'BoardView',
    component: () => import('../views/BoardViewPage.vue')
  },
  {
    path: '/boards/:boardId/edit',
    name: 'BoardEdit',
    component: BuildBoardsPage,
    meta: { requiresAuth: true }
  },
  // The link a developer shares with playtesters. Public, no account, and the
  // edge middleware gives it a real preview.
  {
    path: '/playtest/:gameId',
    name: 'Playtest',
    component: () => import('../views/PlaytestPage.vue')
  },
  // The cards for each cut version, for players to download. Public wherever
  // the game is, like the playtest page.
  {
    path: '/decks/:gameId',
    name: 'Decks',
    component: () => import('../views/DecksPage.vue')
  },
  // A player aid has its own page, so a link to one — from an agent or the
  // game page — opens the sheet instead of the 404 page.
  {
    path: '/aids/:aidId',
    name: 'AidView',
    component: () => import('../views/AidViewPage.vue')
  },
  // Spaces are objects in their own right, like cards.
  {
    path: '/tiles/:tileId',
    name: 'TileDetail',
    component: () => import('../views/TileDetailPage.vue')
  },
  {
    path: '/tiles/:tileId/edit',
    name: 'TileEdit',
    component: () => import('../views/TileDetailPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginPage,
    meta: { requiresGuest: true }
  },
  {
    path: '/register',
    name: 'Register',
    component: RegisterPage,
    meta: { requiresGuest: true }
  },
  {
    path: '/create-board-game',
    name: 'CreateBoardGame',
    component: CreateBoardGamePage,
    meta: { requiresAuth: true }
  },
  {
    path: '/board-game/:id',
    name: 'BoardGameDetail',
    component: BoardGameDetailPage,
    meta: { requiresAuth: true }
  },
  {
    path: '/settings',
    name: 'UserSettings',
    component: UserSettingsPage,
    meta: { requiresAuth: true }
  },
  {
    path: '/cards/:cardId',
    name: 'CardDetail',
    component: CardDetailPage,
    meta: { requiresAuth: true }
  },
  {
    path: '/design',
    name: 'GameDesigner',
    component: GameDesignerPage,
    meta: { requiresAuth: true }
  },
  {
    path: '/cards/:cardId/edit',
    name: 'EditCard',
    component: BuildCardsPage,
    meta: { requiresAuth: true }
  },
  {
    path: '/playtest-review',
    name: 'PlaytestReview',
    component: PlaytestReviewPage,
    meta: { requiresAuth: true }
  },
  // Catch-all. Must stay last — vue-router takes the first match, so anything
  // added below this would never be reached.
  //
  // Deliberately no requiresAuth: a signed-out visitor following a dead link
  // should be told the page is missing, not bounced to /login and then handed
  // a redirect back to a route that does not exist.
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFoundPage
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// Navigation guard
router.beforeEach((to, from, next) => {
  const auth = getAuth();
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const requiresGuest = to.matched.some(record => record.meta.requiresGuest);

  return new Promise((resolve) => {
    onAuthStateChanged(auth, (user) => {
      store.commit('setUser', user);
      if (requiresAuth && !user) {
        // Carry the intended route through the login, so a shared link survives
        // it. Without this, following a link to a game bounced you to login and
        // then dropped you on the home page with the game lost.
        resolve(next({ path: '/login', query: { redirect: to.fullPath } }));
      } else if (requiresGuest && user) {
        // If route requires guest (not logged in) and user is logged in, redirect to home
        // store.commit('setUser', user);
        resolve(next('/'));
      } else {
        // store.commit('setUser', user);
        resolve(next());
      }
    });
  });
});

export default router